/**
 * PostHog Analytics
 *
 * Product analytics for user behavior, feature adoption, and activation funnels.
 * All functions are safe no-ops if PostHog is not initialized (missing API key).
 *
 * IMPORTANT: PostHog is only initialized when the user has accepted cookie
 * consent via the cookie consent banner. If consent has not been given or
 * was declined, initPostHog() is a no-op. This is required by GDPR — we
 * must NOT track users until they explicitly consent.
 */

import posthog from 'posthog-js'
import { isAnalyticsAllowed } from '@/lib/cookie-consent'

let initialized = false

/**
 * Initialize PostHog. Call once at app startup (main.tsx) and again
 * when a public-page visitor accepts cookie consent.
 *
 * Consent logic:
 *   - Inside /app/* (authenticated): always init — ToS/SaaS agreement
 *     accepted at signup covers product analytics.
 *   - Public pages (landing, login): only init if the visitor has
 *     accepted cookies via the consent banner (GDPR requirement).
 *
 * No-op if:
 *   - VITE_POSTHOG_KEY is not set (dev without analytics)
 *   - On a public page and user has not accepted cookies
 *   - Already initialized
 */
export function initPostHog(): void {
  if (initialized) return

  const apiKey = import.meta.env['VITE_POSTHOG_KEY']
  const host = import.meta.env['VITE_POSTHOG_HOST'] ?? 'https://us.i.posthog.com'

  if (!apiKey) return

  // Inside the app: ToS covers analytics, no consent check needed.
  // Public pages: require explicit cookie consent from the banner.
  const isAppRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/app')
  if (!isAppRoute && !isAnalyticsAllowed()) return

  posthog.init(apiKey, {
    api_host: host,
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    capture_exceptions: true,
    disable_session_recording: true,
    persistence: 'localStorage+cookie',
  })

  initialized = true
}

/**
 * Identify a user and associate them with their organization.
 */
export function identifyUser(
  userId: string,
  properties: {
    email: string
    name?: string | null
    organizationId?: string | null
    organizationName?: string | null
    role: string
    isPlatformAdmin: boolean
    mfaEnrolled?: boolean
  }
): void {
  if (!initialized) return

  posthog.identify(userId, {
    email: properties.email,
    name: properties.name ?? undefined,
    role: properties.role,
    is_platform_admin: properties.isPlatformAdmin,
    mfa_enrolled: properties.mfaEnrolled ?? false,
  })

  if (properties.organizationId) {
    posthog.group('organization', properties.organizationId, {
      name: properties.organizationName ?? undefined,
    })
  }
}

/**
 * Reset user identity on sign-out.
 */
export function resetUser(): void {
  if (!initialized) return
  posthog.reset()
}

/**
 * Capture a custom event. Fire-and-forget.
 */
export function captureEvent(
  eventName: string,
  properties?: Record<string, unknown>
): void {
  if (!initialized) return
  posthog.capture(eventName, properties)
}

/**
 * Check if a feature flag is enabled for the current user.
 */
export function isFeatureFlagEnabled(flagName: string): boolean {
  if (!initialized) return false
  return posthog.isFeatureEnabled(flagName) ?? false
}

/**
 * Get the raw PostHog instance (for hook subscriptions).
 */
export function getPostHogInstance(): typeof posthog | null {
  if (!initialized) return null
  return posthog
}
